import { createContext, useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { WeekSwitch } from '@/components/common/WeekSwitch';
import { BoardViewMode, END_HOUR, SidebarMode, START_HOUR } from './config';
import { TNote } from '../../types';
import { CellCoordinates, GridData } from './types';
import { useNotesContext } from '../../hooks/useNotesContext';
import { useNotesFilterInit } from '../../hooks/useNotesFilterInit';
import { teams } from '../../data/task-manager';
import PublicAPI from '@/api/TreedisPublicDAO';
import { useNotesStore } from '../../stores/notes.store';

type NotesBoardContext = {
  isBacklogOpen: boolean;
  toggleBacklogOpen: (isOpen?: boolean) => void;
  weekRange: [Date, Date];
  setWeekRange: (range: [Date, Date]) => void;
  viewMode: BoardViewMode;
  setViewMode: (mode: BoardViewMode) => void;
  onBoardClose?: () => void;
  onBoardShrink?: () => void;
  draggingCardId: string | null;
  setDraggingCardId: (id: string | null) => void;
  draggingCardData: TNote | null;
  moveNoteToGrid: (noteId: string, coordinates: CellCoordinates) => void;
  moveNoteToBacklog: (noteId: string) => void;
  gridData: GridData;
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  boardFilters: ReturnType<typeof useNotesFilterInit>;
  applyFiltersToBacklog: boolean;
  setApplyFiltersToBacklog: (value: boolean) => void;
  boardSearch: string;
  setBoardSearch: (value: string) => void;
};

export const NotesBoardContext = createContext<NotesBoardContext>(null);

export type BoardProviderProps = {
  onClose?: () => void;
  onShrink?: () => void;
};
export function NotesBoardProvider({
  children,
  onClose,
  onShrink,
}: BoardProviderProps & { children: React.ReactNode }) {
  const { isBoardOpen, collaborators } = useNotesContext();
  const [viewMode, setViewMode] = useState<BoardViewMode>(BoardViewMode.Week);
  const [weekRange, setWeekRange] = useState(() => WeekSwitch.getWeekRange());
  const [isBacklogOpen, setIsBacklogOpen] = useState(false);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(SidebarMode.Backlog);
  const boardFilters = useNotesFilterInit();
  const [applyFiltersToBacklog, setApplyFiltersToBacklog] = useState(true);
  const [boardSearch, setBoardSearch] = useState('');

  const notes = useNotesStore((state) => state.notes);
  const { updateNote } = useNotesStore((state) => state.actions);

  // 8:00, 8:30, 9:00 etc. in minutes passed from 00:00
  const timeSlots = Array.from(
    { length: (END_HOUR * 60 + 30 - START_HOUR * 60) / 30 + 1 },
    (_, i) => START_HOUR * 60 + i * 30,
  );

  const days = Array.from({ length: 5 }, (_, i) => {
    const weekStart = weekRange[0];
    const startOfWeekDay = dayjs(weekStart).startOf('day');
    const weekday = startOfWeekDay.add(i, 'day');
    return weekday.unix();
  });

  // For now show only teams
  const assignees = useMemo(() => {
    return [
      ...teams,
      // ...collaborators.map((c) => ({ name: c.name, id: c.id.toString() }))
    ];
  }, [collaborators]);

  useEffect(() => {
    if (!isBoardOpen) {
      setSidebarMode(SidebarMode.Backlog);
    }
  }, [isBoardOpen]);

  const draggingCardData = useMemo(() => {
    const note = notes.find((n) => n.id.toString() === draggingCardId);
    return note;
  }, [draggingCardId, notes]);

  const toggleBacklogOpen = (isOpen?: boolean) => {
    setIsBacklogOpen((prev) => (isOpen === undefined ? !prev : isOpen));
  };

  const moveNoteToBacklog = async (noteId: string) => {
    const note = notes.find((n) => n.id.toString() === noteId);

    try {
      const result = await PublicAPI.moveBoardNote({
        data: {
          noteId: note.id,
          backlogEnabled: true,
        },
      });

      if (result.message === 'Success') {
        const savedNote = result.note;
        updateNote(savedNote.id, {
          backlogEnabled: savedNote.backlogEnabled,
        });

        setSidebarMode(SidebarMode.Backlog);
      }
    } catch (error) {
      toast.error(`Failed to put Note to backlog`);
    }
  };

  /**
   * Constructs a due date from timeSlot index and day index
   * @param timeSlotIndex - Index in the timeSlots array (30-minute intervals starting from 8:00 AM)
   * @param dayIndex - Index in the days array (Unix timestamps for the week)
   * @returns ISO string representation of the due date
   */
  const constructDueDateFromGridCoordinates = (timeSlotIndex: number, dayIndex: number): string => {
    // Get the timeSlot minutes (minutes from 00:00)
    const timeSlotMinutes = timeSlots[timeSlotIndex];

    // Convert minutes to hours and minutes
    const dueHours = Math.floor(timeSlotMinutes / 60);
    const dueMinutes = timeSlotMinutes % 60;
    // Get the day Unix timestamp
    const dayTimestamp = days[dayIndex];

    // Create a dayjs object from the day timestamp and set the time
    const dueDate = dayjs
      .unix(dayTimestamp)
      .hour(dueHours)
      .minute(dueMinutes)
      .second(0)
      .millisecond(0);

    // Return as ISO string (compatible with note.dueDate field)
    return dueDate.toISOString();
  };

  const moveNoteToGrid = async (noteId: string, coordinates: CellCoordinates) => {
    const note = notes.find((n) => n.id.toString() === noteId);
    const assignee = assignees[coordinates.column];
    const dueDate = constructDueDateFromGridCoordinates(coordinates.row, coordinates.group);

    try {
      const result = await PublicAPI.moveBoardNote({
        data: {
          noteId: note.id,
          backlogEnabled: false,
          dueDate,
          assignee: assignee.id,
        },
      });
      if (result.message === 'Success') {
        const savedNote = result.note;
        updateNote(savedNote.id, {
          backlogEnabled: savedNote.backlogEnabled,
          dueDate: savedNote.dueDate,
          assignee: savedNote.assignee,
        });
      }
    } catch (error) {
      toast.error(`Failed to move Note on board`);
    }
  };

  return (
    <NotesBoardContext.Provider
      value={{
        viewMode,
        setViewMode,
        isBacklogOpen,
        setWeekRange,
        toggleBacklogOpen,
        weekRange,
        onBoardClose: onClose,
        onBoardShrink: onShrink,
        draggingCardId,
        setDraggingCardId,
        draggingCardData,
        moveNoteToGrid,
        moveNoteToBacklog,
        sidebarMode,
        setSidebarMode,
        boardFilters,
        applyFiltersToBacklog,
        setApplyFiltersToBacklog,
        boardSearch,
        setBoardSearch,
        gridData: {
          days,
          timeSlots,
          assignees,
        },
      }}
    >
      {children}
    </NotesBoardContext.Provider>
  );
}
